import { compareItems, type RankingInfo, rankItem } from '@tanstack/match-sorter-utils';
import {
  type ColumnDef,
  type ColumnFiltersState,
  type FilterFn,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingFn,
  sortingFns,
  useReactTable,
} from '@tanstack/react-table';
import { useEffect, useState } from 'react';

declare module '@tanstack/react-table' {
  interface FilterFns {
    fuzzy: FilterFn<unknown>;
  }
  interface FilterMeta {
    itemRank: RankingInfo;
  }
}

// Custom fuzzy filter using match-sorter ranking
const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value);
  addMeta({ itemRank });
  return itemRank.passed;
};

// Sort by fuzzy rank, fall back to alphanumeric
export const fuzzySort: SortingFn<any> = (rowA, rowB, columnId) => {
  let dir = 0;
  if (rowA.columnFiltersMeta[columnId]) {
    dir = compareItems(
      rowA.columnFiltersMeta[columnId]!.itemRank,
      rowB.columnFiltersMeta[columnId]!.itemRank
    );
  }
  return dir === 0 ? sortingFns.alphanumeric(rowA, rowB, columnId) : dir;
};

export type UseTableProps<T> = {
  columns: ColumnDef<T, any>[];
  data: T[];
  /** Global fuzzy search string */
  filter?: string;
  /** Per-column filter state (controlled externally) */
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: (filters: ColumnFiltersState) => void;
  pageSize?: number;
};

export const useTable = <T>({
  columns,
  data,
  filter,
  columnFilters: externalColumnFilters,
  onColumnFiltersChange,
  pageSize = 10,
}: UseTableProps<T>) => {
  const [internalColumnFilters, setInternalColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState<string | undefined>(filter);

  const columnFilters = externalColumnFilters ?? internalColumnFilters;
  const setColumnFilters = onColumnFiltersChange ?? setInternalColumnFilters;

  useEffect(() => {
    setGlobalFilter(filter);
  }, [filter]);

  const table = useReactTable({
    data,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: (updaterOrValue) => {
      if (typeof updaterOrValue === 'function') {
        setColumnFilters(updaterOrValue(columnFilters));
      } else {
        setColumnFilters(updaterOrValue);
      }
    },
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: 'fuzzy',
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    autoResetPageIndex: false,
    initialState: {
      pagination: { pageSize },
    },
  });

  return { table, globalFilter, setGlobalFilter, columnFilters, setColumnFilters };
};
